/** A declared, non-sensitive field included in the visitor's next message. */
export interface ComposerField {
  id: string;
  label: string;
  value: string;
  valueLabel?: string;
}

/** Visitor-selected content, an intended action or entered details, staged without sending. */
export interface ComposerItemInput {
  id: string;
  label: string;
  instruction: string;
  context: Record<string, unknown>;
  fields?: ComposerField[];
}

export interface ComposerItem extends ComposerItemInput {
  surfaceId: string;
  contentId?: string;
}

export function sameComposerItem(a: ComposerItem, b: ComposerItem): boolean {
  return a.surfaceId === b.surfaceId && a.id === b.id;
}

/** Replace a repeated selection in place, keeping the visitor's reading order. */
export function addComposerItem(items: ComposerItem[], item: ComposerItem): ComposerItem[] {
  if (!item.id.trim() || !item.label.trim() || !item.instruction.trim()) {
    return items;
  }
  const index = items.findIndex((existing) => sameComposerItem(existing, item));
  if (index < 0) {
    return [...items, item];
  }
  return items.map((existing, position) => (position === index ? item : existing));
}

/** Only the exact submitted versions leave the draft; newer additions stay. */
export function consumeComposerItems(
  items: ComposerItem[],
  submitted: ComposerItem[],
): ComposerItem[] {
  return items.filter((item) => !submitted.includes(item));
}

/** A failed send returns its fragments without replacing newer edits. */
export function restoreComposerItems(
  items: ComposerItem[],
  submitted: ComposerItem[],
): ComposerItem[] {
  return [
    ...submitted.filter((item) => !items.some((current) => sameComposerItem(current, item))),
    ...items,
  ];
}

/** Visible selected details in the composer. Values stay separate from the visitor's editable prose. */
export function composerItemText(item: ComposerItem): string {
  const fields = item.fields?.filter((field) => field.value.trim());
  if (!fields?.length) {
    return item.label;
  }
  return fields.map((field) => field.valueLabel ?? `${field.label} ${field.value}`).join(', ');
}

export function composerMessage(text: string, items: ComposerItem[]): string {
  return [text.trim(), ...items.map((item) => item.instruction)].filter(Boolean).join('\n');
}

/** The same envelope carries selected content and input groups; neither commits a business action. */
export function composerAction(items: ComposerItem[]) {
  if (!items.length) return undefined;
  return {
    surfaceId: items[0].surfaceId,
    name: 'Use message draft',
    context: { items },
  };
}
